import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { StatusLozengeButton } from '../../../../../../../common/ui/status-lozenge-button';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphCampaign_AtlassianSearchPage$key } from './__generated__/searchGraphCampaign_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	campaignContainer: {
		width: '100%',
	},
	resultWrapper: {
		flex: '1',
		minWidth: '0px',
	},
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

export const SearchGraphCampaign = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphCampaign_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage, formatDate } = useIntl();

	const data = useFragment(
		graphql`
			fragment searchGraphCampaign_AtlassianSearchPage on SearchResultGraphDocument {
				id
				url
				title
				lastModifiedDate
				excerpt
				entity {
					... on ExternalCampaign {
						status
						endDate
						lastUpdated
						owners {
							thirdPartyUser {
								name
							}
						}
						createdBy {
							thirdPartyUser {
								name
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { id, url, title, lastModifiedDate, excerpt, entity } = data;

	const ownerName =
		entity?.owners?.[0]?.thirdPartyUser?.name ?? entity?.createdBy?.thirdPartyUser?.name;

	const lastModifiedComponent = (
		<LastModified lastModified={entity?.lastUpdated ?? lastModifiedDate} />
	);

	const description = <DescriptionBody description={enrichExcerpt(excerpt)} />;

	const statusText = entity?.status ?? '';

	const endDateValue = entity?.endDate ? new Date(entity.endDate) : null;
	const formattedEndDate = endDateValue
		? formatDate(endDateValue, {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
			})
		: '';
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const endDateMessage =
		endDateValue && endDateValue >= today ? messages.endsOn : messages.endedOn;

	const endDatePopupContent = endDateValue ? (
		<Box xcss={styles.content}>
			<FormattedMessage {...endDateMessage} values={{ date: formattedEndDate }} />
		</Box>
	) : undefined;

	const headerMetadataParts = [
		formatMessage(messages.campaignLabel),
		// Note: parent/location omitted — ExternalCampaign has containerId only (no hydration)
		['owner', ownerName],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<Flex gap="space.100" alignItems="start" role="listitem" xcss={styles.campaignContainer}>
			<Box xcss={styles.resultWrapper}>
				<SearchResult
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					icon={icon}
					description={description}
					headerMetadataParts={headerMetadataParts}
					title={enrichExcerpt(title)}
					role="none"
					url={url}
					isThirdParty
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					position={position}
					partial={data}
					appMode={appMode}
					footer={footerComponent}
					hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
					mainContentWidth={mainContentWidth}
				/>
			</Box>
			{statusText && <StatusLozengeButton statusText={statusText} popupContent={endDatePopupContent} />}
		</Flex>
	);
};
